import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { AppBanner } from '../components/AppBanner';
import {
  useStoryDetail,
  useAddEntry,
  useStoryDetailAnonymous,
  useAddEntryAnonymous,
} from '../hooks/useStories';
import { useAuthStore } from '../stores/auth.store';
import { useAnonymousStore } from '../stores/anonymous.store';

const WritePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isAnonymousMode = searchParams.get('anon') === '1';

  // Auth state
  const user = useAuthStore((state) => state.user);

  // Anonymous state
  const { anonymousId } = useAnonymousStore();

  // Use appropriate hooks based on mode
  const authQuery = useStoryDetail(isAnonymousMode ? undefined : id);
  const anonQuery = useStoryDetailAnonymous(
    isAnonymousMode ? id : undefined,
    isAnonymousMode ? anonymousId : undefined
  );

  const authMutation = useAddEntry();
  const anonMutation = useAddEntryAnonymous();

  // Select active data based on mode
  const { data, isLoading, error, refetch } = isAnonymousMode ? anonQuery : authQuery;
  const addEntryMutation = isAnonymousMode ? anonMutation : authMutation;

  const [text, setText] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submittedText, setSubmittedText] = useState<string | null>(null);

  const MAX_LENGTH = 280;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !text.trim()) return;

    const submittedContent = text.trim();

    try {
      setSubmitError('');

      if (isAnonymousMode) {
        await anonMutation.mutateAsync({
          storyId: id,
          anonymousId,
          text: submittedContent,
        });
      } else {
        await authMutation.mutateAsync({ storyId: id, text: submittedContent });
      }

      setSubmittedText(submittedContent);
      setText('');
      refetch();
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to add your sentence');
    }
  };

  const handleShare = async () => {
    if (!data?.story) return;

    const shareUrl = `${window.location.origin}/join/${data.story.code}`;
    const shareText = 'Join our collaborative story on StoryChain!';

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join StoryChain Story',
          text: shareText,
          url: shareUrl,
        });
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (error || !data?.story) {
    return (
      <Layout title="Story Not Found">
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">🔍</span>
          <h2 className="text-xl font-semibold mb-2">Story not found</h2>
          <p className="text-gray-500">
            This story may have been deleted.
          </p>
        </div>
      </Layout>
    );
  }

  const { story, previousEntry, canWrite } = data;

  // Redirect to completed page if story is done
  if (story.status === 'completed') {
    navigate(`/story/${id}/completed`, { replace: true });
    return null;
  }

  const currentTurnUser = story.participants.find(
    (p) => p.order === story.currentTurn
  );

  // Find my participant based on mode
  const myParticipant = isAnonymousMode
    ? story.participants.find((p) => p.anonymousId === anonymousId)
    : story.participants.find((p) => p.userId === user?.id);

  // Get display name for a participant
  const getParticipantName = (p: typeof story.participants[0], index: number) => {
    if (p.displayName) return p.displayName;
    if (p.user?.name) return p.user.name;
    return `Writer ${index + 1}`;
  };

  // Check if participant is me
  const isMe = (p: typeof story.participants[0]) => {
    if (isAnonymousMode) {
      return p.anonymousId === anonymousId;
    }
    return p.userId === user?.id;
  };

  return (
    <Layout title="Write Your Part">
      <div className="space-y-6">
        <AppBanner />

        {/* Progress */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Story Progress</span>
            <span className="text-sm font-medium">
              Turn {story.currentTurn} of {story.maxParticipants}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all"
              style={{ width: `${((story.currentTurn - 1) / story.maxParticipants) * 100}%` }}
            />
          </div>
        </div>

        {/* Creator's hint - shown if enabled */}
        {story.showHintToParticipants && story.creatorHint && (
          <div className="bg-amber-50 rounded-2xl p-4 shadow-sm border border-amber-200">
            <h3 className="text-sm font-medium text-amber-700 mb-2">
              💡 Creator's vision
            </h3>
            <p className="text-amber-800 italic">
              "{story.creatorHint}"
            </p>
          </div>
        )}

        {/* Previous sentence */}
        {previousEntry && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Previous sentence
            </h3>
            <p className="text-lg leading-relaxed italic text-gray-700">
              "{previousEntry.text}"
            </p>
          </div>
        )}

        {/* Writing area, confirmation, or waiting */}
        {canWrite ? (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Your turn! Continue the story...
            </h3>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_LENGTH))}
              placeholder="Write your sentence..."
              className="w-full h-32 p-3 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              maxLength={MAX_LENGTH}
            />
            <div className="flex justify-between items-center mt-3">
              <span className={`text-sm ${text.length > MAX_LENGTH - 20 ? 'text-orange-500' : 'text-gray-400'}`}>
                {text.length}/{MAX_LENGTH}
              </span>
              <button
                type="submit"
                disabled={!text.trim() || addEntryMutation.isPending}
                className="bg-primary-600 text-white px-6 py-2 rounded-xl font-semibold disabled:opacity-50 hover:bg-primary-700 transition"
              >
                {addEntryMutation.isPending ? 'Sending...' : 'Submit'}
              </button>
            </div>
            {submitError && (
              <p className="text-red-500 text-sm mt-2">{submitError}</p>
            )}
          </form>
        ) : submittedText ? (
          // Show confirmation after submission
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="text-center mb-4">
              <span className="text-4xl mb-3 block">✅</span>
              <h3 className="font-semibold text-lg mb-1">Your part is submitted!</h3>
              <p className="text-gray-500 text-sm">
                Waiting for {story.maxParticipants - story.currentTurn + 1} more writers
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-500 mb-1">You wrote:</p>
              <p className="text-gray-800 italic">"{submittedText}"</p>
            </div>

            <button
              onClick={handleShare}
              className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition flex items-center justify-center gap-2"
            >
              <span>📤</span>
              Invite More Friends
            </button>

            <button
              onClick={() => refetch()}
              className="w-full mt-3 text-primary-600 font-medium hover:underline"
            >
              Refresh Status
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
            <span className="text-4xl mb-3 block">⏳</span>
            <h3 className="font-semibold text-lg mb-1">Waiting for your turn</h3>
            <p className="text-gray-500 text-sm">
              {currentTurnUser
                ? `${getParticipantName(currentTurnUser, currentTurnUser.order - 1)} is writing...`
                : 'Waiting for the next writer...'}
            </p>
            {myParticipant && (
              <p className="text-gray-400 text-sm mt-3">
                You are #{myParticipant.order} in line
              </p>
            )}
            <button
              onClick={() => refetch()}
              className="mt-4 text-primary-600 font-medium hover:underline"
            >
              Refresh
            </button>
          </div>
        )}

        {/* Participants */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Writers</h3>
          <div className="flex flex-wrap gap-2">
            {story.participants.map((p, i) => (
              <div
                key={i}
                className={`px-3 py-1 rounded-full text-sm ${
                  p.order < story.currentTurn
                    ? 'bg-green-100 text-green-700'
                    : p.order === story.currentTurn
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {p.order === story.currentTurn && '✍️ '}
                {getParticipantName(p, i)}
                {isMe(p) && ' (you)'}
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400">
          Download the app for push notifications when it's your turn!
        </p>
      </div>
    </Layout>
  );
};

export default WritePage;
